import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  BookMarked,
  ExternalLink,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronRight,
  Plus,
  Loader2,
  Search,
} from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { normalizeResourceUrl, linkLabel } from './resourceUtils'

export { normalizeResourceUrl, linkLabel }

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  border: '1.5px solid var(--color-border)',
  borderRadius: '8px',
  fontSize: '13px',
  outline: 'none',
  backgroundColor: 'var(--color-bg-body)',
  color: 'var(--color-text-primary)',
  boxSizing: 'border-box',
}

const labelStyle = {
  display: 'block',
  fontSize: '11px',
  fontWeight: '600',
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '4px',
}

function linkMatchesQuery(link, qLower) {
  if (!qLower) return true
  const hay = [
    linkLabel(link),
    link.url || '',
    (link.note || '').trim(),
  ]
    .join(' ')
    .toLowerCase()
  return hay.includes(qLower)
}

function ResourceLinkCard({
  link,
  categories,
  canEditResources,
  editingId,
  setEditingId,
  editDraft,
  setEditDraft,
  onSaved,
  onRequestDelete,
}) {
  const [saving, setSaving] = useState(false)
  const [localError, setLocalError] = useState(null)
  const isEditing = editingId === link.id

  const startEdit = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setLocalError(null)
    setEditDraft({
      display_name: link.display_name || '',
      url: link.url,
      note: link.note || '',
      category_id: link.category_id,
    })
    setEditingId(link.id)
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditDraft(null)
    setLocalError(null)
  }

  const saveEdit = async (e) => {
    e.preventDefault()
    setLocalError(null)
    const normalized = normalizeResourceUrl(editDraft.url)
    if (!normalized.ok) {
      setLocalError(normalized.error)
      return
    }
    const displayName = editDraft.display_name?.trim() || null
    setSaving(true)
    const { error } = await supabase
      .from('resource_links')
      .update({
        display_name: displayName,
        url: normalized.url,
        note: editDraft.note?.trim() || null,
        category_id: editDraft.category_id,
      })
      .eq('id', link.id)
    setSaving(false)
    if (error) {
      setLocalError(error.message || 'Save failed')
      return
    }
    setEditingId(null)
    setEditDraft(null)
    onSaved()
  }

  const requestRemove = (e) => {
    e.preventDefault()
    e.stopPropagation()
    onRequestDelete(link)
  }

  const cardShell = {
    backgroundColor: 'var(--color-surface-raised)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
    boxShadow: 'var(--shadow-sm)',
    minHeight: '88px',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    transition: 'box-shadow 0.15s, border-color 0.15s',
  }

  if (isEditing) {
    return (
      <div style={{ ...cardShell, padding: '14px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div>
            <label style={labelStyle}>Display name (optional)</label>
            <input
              value={editDraft.display_name}
              onChange={(e) => setEditDraft((d) => ({ ...d, display_name: e.target.value }))}
              placeholder="Shown in UI; leave empty to use URL"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>URL</label>
            <input
              value={editDraft.url}
              onChange={(e) => setEditDraft((d) => ({ ...d, url: e.target.value }))}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Note (optional)</label>
            <input
              value={editDraft.note}
              onChange={(e) => setEditDraft((d) => ({ ...d, note: e.target.value }))}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Category</label>
            <select
              value={editDraft.category_id}
              onChange={(e) => setEditDraft((d) => ({ ...d, category_id: e.target.value }))}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          {localError && (
            <div style={{ fontSize: '12px', color: '#ef4444' }}>{localError}</div>
          )}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              type="button"
              disabled={saving}
              onClick={saveEdit}
              style={{
                padding: '8px 14px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: 'var(--color-accent)',
                color: '#0f172a',
                fontWeight: '600',
                fontSize: '13px',
                cursor: saving ? 'wait' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              {saving && <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} />}
              Save
            </button>
            <button
              type="button"
              onClick={cancelEdit}
              style={{
                padding: '8px 14px',
                borderRadius: '8px',
                border: '1px solid var(--color-border)',
                backgroundColor: 'transparent',
                color: 'var(--color-text-secondary)',
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={requestRemove}
              style={{
                marginLeft: 'auto',
                padding: '8px 12px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: 'rgba(239,68,68,0.12)',
                color: '#ef4444',
                fontSize: '13px',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Trash2 size={14} /> Delete
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      style={cardShell}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = 'var(--shadow-lg)'
        e.currentTarget.style.borderColor = 'var(--color-accent)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'var(--shadow-sm)'
        e.currentTarget.style.borderColor = 'var(--color-border)'
      }}
    >
      <a
        href={link.url}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          flex: 1,
          padding: canEditResources ? '14px 44px 14px 16px' : '14px 16px',
          textDecoration: 'none',
          color: 'var(--color-text-primary)',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          minWidth: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
          <span
            style={{
              fontSize: '14px',
              fontWeight: '600',
              lineHeight: 1.35,
              wordBreak: 'break-word',
            }}
          >
            {linkLabel(link)}
          </span>
          <ExternalLink size={16} style={{ flexShrink: 0, color: 'var(--color-accent)', opacity: 0.85 }} />
        </div>
        {link.note?.trim() && (
          <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', lineHeight: 1.4 }}>{link.note.trim()}</span>
        )}
      </a>
      {canEditResources && (
        <button
          type="button"
          onClick={startEdit}
          title="Edit link"
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            width: '32px',
            height: '32px',
            borderRadius: '6px',
            border: '1px solid var(--color-border)',
            backgroundColor: 'var(--color-surface)',
            color: 'var(--color-text-secondary)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Pencil size={14} />
        </button>
      )}
    </div>
  )
}

export default function ResourcesPage({ canEditResources = true }) {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [showAddPanel, setShowAddPanel] = useState(true)
  const [resourceSearch, setResourceSearch] = useState('')

  const [addDisplayName, setAddDisplayName] = useState('')
  const [addUrl, setAddUrl] = useState('')
  const [addNote, setAddNote] = useState('')
  const [addCategoryId, setAddCategoryId] = useState('')
  const [addError, setAddError] = useState(null)
  const [adding, setAdding] = useState(false)

  const [editingLinkId, setEditingLinkId] = useState(null)
  const [editDraft, setEditDraft] = useState(null)
  const editingLinkIdRef = useRef(null)
  editingLinkIdRef.current = editingLinkId

  const [linkDeleteConfirm, setLinkDeleteConfirm] = useState(null)
  const [linkDeleteBusy, setLinkDeleteBusy] = useState(false)
  const [linkDeleteError, setLinkDeleteError] = useState(null)

  const fetchResources = useCallback(async ({ showSpinner } = { showSpinner: true }) => {
    setLoadError(null)
    if (showSpinner) setLoading(true)
    const { data, error } = await supabase
      .from('resource_categories')
      .select(
        'id, name, sort_order, resource_links(id, category_id, display_name, url, note, created_at)'
      )
      .order('sort_order')

    if (error) {
      setLoadError(error.message)
      setCategories([])
    } else {
      const sorted = (data || []).map((c) => ({
        ...c,
        resource_links: [...(c.resource_links || [])].sort(
          (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0)
        ),
      }))
      setCategories(sorted)
      setAddCategoryId((prev) => {
        if (prev && sorted.some((c) => c.id === prev)) return prev
        return sorted[0]?.id || ''
      })
    }
    if (showSpinner) setLoading(false)
  }, [])

  useEffect(() => {
    fetchResources({ showSpinner: true })
  }, [fetchResources])

  const searchLower = resourceSearch.trim().toLowerCase()

  const filteredCategories = useMemo(() => {
    if (!searchLower) return categories
    return categories
      .map((cat) => {
        const nameMatch = cat.name.toLowerCase().includes(searchLower)
        const links = (cat.resource_links || []).filter(
          (link) => nameMatch || linkMatchesQuery(link, searchLower)
        )
        return { ...cat, resource_links: links }
      })
      .filter((cat) => (cat.resource_links || []).length > 0)
  }, [categories, searchLower])

  const onSaved = useCallback(() => {
    fetchResources({ showSpinner: false })
  }, [fetchResources])

  const openLinkDeleteModal = useCallback((link) => {
    setLinkDeleteError(null)
    setLinkDeleteConfirm({
      id: link.id,
      label: linkLabel(link),
      url: link.url,
    })
  }, [])

  const closeLinkDeleteModal = useCallback(() => {
    if (linkDeleteBusy) return
    setLinkDeleteConfirm(null)
    setLinkDeleteError(null)
  }, [linkDeleteBusy])

  const confirmLinkDelete = useCallback(async () => {
    if (!linkDeleteConfirm) return
    setLinkDeleteError(null)
    setLinkDeleteBusy(true)
    const { error } = await supabase.from('resource_links').delete().eq('id', linkDeleteConfirm.id)
    setLinkDeleteBusy(false)
    if (error) {
      setLinkDeleteError(error.message || 'Delete failed')
      return
    }
    const deletedId = linkDeleteConfirm.id
    if (editingLinkIdRef.current === deletedId) {
      setEditingLinkId(null)
      setEditDraft(null)
    }
    setLinkDeleteConfirm(null)
    fetchResources({ showSpinner: false })
  }, [linkDeleteConfirm, fetchResources])

  const addLink = async (e) => {
    e.preventDefault()
    setAddError(null)
    if (!addCategoryId) {
      setAddError('Choose a category first, or use Manage categories to add one.')
      return
    }
    const normalized = normalizeResourceUrl(addUrl)
    if (!normalized.ok) {
      setAddError(normalized.error)
      return
    }
    const displayName = addDisplayName.trim() || null
    setAdding(true)
    const { error } = await supabase.from('resource_links').insert({
      category_id: addCategoryId,
      display_name: displayName,
      url: normalized.url,
      note: addNote.trim() || null,
    })
    setAdding(false)
    if (error) {
      setAddError(error.message || 'Could not add link')
      return
    }
    setAddDisplayName('')
    setAddUrl('')
    setAddNote('')
    fetchResources({ showSpinner: false })
  }

  const hasAnyLinks = categories.some((c) => (c.resource_links || []).length > 0)
  const showNoSearchResults =
    !loading && !loadError && searchLower && filteredCategories.length === 0 && hasAnyLinks

  return (
    <div style={{ padding: '28px 32px', maxWidth: '1200px', margin: '0 auto' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ marginBottom: '28px' }}>
        <h1
          style={{
            fontSize: '28px',
            fontWeight: '700',
            margin: '0 0 4px 0',
            color: 'var(--color-text-primary)',
            letterSpacing: '-0.02em',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <BookMarked size={28} strokeWidth={2} style={{ opacity: 0.9 }} />
          Resources
        </h1>
        <p style={{ color: 'var(--color-text-muted)', margin: 0, fontSize: '14px' }}>
          Quick links by category. Open in a new tab.
        </p>
      </div>

      {!loading && !loadError && categories.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>Search</label>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              maxWidth: '420px',
              padding: '8px 12px',
              border: '1.5px solid var(--color-border)',
              borderRadius: '8px',
              backgroundColor: 'var(--color-bg-body)',
            }}
          >
            <Search size={18} style={{ flexShrink: 0, color: 'var(--color-text-muted)' }} />
            <input
              type="search"
              value={resourceSearch}
              onChange={(e) => setResourceSearch(e.target.value)}
              placeholder="Filter by name, URL, note, or category…"
              aria-label="Search resources"
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: '14px',
                color: 'var(--color-text-primary)',
                minWidth: 0,
              }}
            />
          </div>
        </div>
      )}

      {loading && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            color: 'var(--color-text-muted)',
            padding: '24px 0',
          }}
        >
          <Loader2 size={20} style={{ animation: 'spin 0.8s linear infinite' }} />
          Loading resources…
        </div>
      )}

      {!loading && loadError && (
        <div
          style={{
            padding: '16px',
            borderRadius: 'var(--radius-lg)',
            backgroundColor: 'rgba(239,68,68,0.08)',
            color: '#b91c1c',
            fontSize: '14px',
            marginBottom: '20px',
          }}
        >
          {loadError}
        </div>
      )}

      {canEditResources && !loading && (
        <div
          style={{
            backgroundColor: 'var(--color-surface)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--color-border)',
            boxShadow: 'var(--shadow-sm)',
            marginBottom: '20px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'stretch',
              background: 'var(--color-surface-raised)',
              borderBottom: showAddPanel ? '1px solid var(--color-border)' : 'none',
            }}
          >
            <button
              type="button"
              onClick={() => setShowAddPanel((v) => !v)}
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '14px 18px',
                border: 'none',
                background: 'transparent',
                color: 'var(--color-text-primary)',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              {showAddPanel ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              <Plus size={16} />
              Add resource link
            </button>
            <Link
              to="/resources/categories"
              onClick={(e) => e.stopPropagation()}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                flexShrink: 0,
                padding: '14px 18px',
                border: 'none',
                borderLeft: '1px solid var(--color-border)',
                backgroundColor: 'transparent',
                color: '#111827',
                fontWeight: '700',
                fontSize: '14px',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              <Plus size={16} strokeWidth={2.25} style={{ flexShrink: 0 }} aria-hidden />
              Manage categories
            </Link>
          </div>
          {showAddPanel && (
            <form onSubmit={addLink} style={{ padding: '18px' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '14px',
                  marginBottom: '14px',
                }}
              >
                <div>
                  <label style={labelStyle}>Display name (optional)</label>
                  <input
                    value={addDisplayName}
                    onChange={(e) => setAddDisplayName(e.target.value)}
                    placeholder="Leave empty to show URL"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>URL</label>
                  <input
                    value={addUrl}
                    onChange={(e) => setAddUrl(e.target.value)}
                    placeholder="https://…"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Category</label>
                  <select
                    value={addCategoryId}
                    onChange={(e) => setAddCategoryId(e.target.value)}
                    style={{ ...inputStyle, cursor: 'pointer' }}
                  >
                    {categories.length === 0 && <option value="">No categories yet</option>}
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Note (optional)</label>
                  <input value={addNote} onChange={(e) => setAddNote(e.target.value)} style={inputStyle} />
                </div>
              </div>
              {addError && <div style={{ fontSize: '13px', color: '#ef4444', marginBottom: '10px' }}>{addError}</div>}
              <button
                type="submit"
                disabled={adding || categories.length === 0}
                style={{
                  padding: '10px 18px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: 'var(--color-accent)',
                  color: '#0f172a',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: adding || categories.length === 0 ? 'not-allowed' : 'pointer',
                  opacity: categories.length === 0 ? 0.5 : 1,
                }}
              >
                {adding ? 'Adding…' : 'Add link'}
              </button>
            </form>
          )}
        </div>
      )}

      {!loading && !loadError && categories.length === 0 && (
        <p style={{ color: 'var(--color-text-muted)', fontSize: '15px' }}>
          No resources yet.
          {canEditResources && (
            <>
              {' '}
              Open{' '}
              <Link
                to="/resources/categories"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: '#111827',
                  fontWeight: 700,
                  textDecoration: 'none',
                }}
              >
                <Plus size={14} strokeWidth={2.25} aria-hidden />
                Manage categories
              </Link>{' '}
              to add a category, then add links here.
            </>
          )}
        </p>
      )}

      {showNoSearchResults && (
        <p style={{ color: 'var(--color-text-muted)', fontSize: '15px', marginBottom: '20px' }}>
          No links match “{resourceSearch.trim()}”. Try different text or clear the search.
        </p>
      )}

      {linkDeleteConfirm && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="link-delete-confirm-title"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15,23,42,0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1110,
            padding: '20px',
          }}
          onClick={closeLinkDeleteModal}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--color-surface)',
              borderRadius: 'var(--radius-xl)',
              border: '1px solid var(--color-border)',
              boxShadow: 'var(--shadow-xl)',
              maxWidth: '440px',
              width: '100%',
            }}
          >
            <div style={{ padding: '22px 24px 12px' }}>
              <h2
                id="link-delete-confirm-title"
                style={{
                  margin: 0,
                  fontSize: '18px',
                  fontWeight: '700',
                  color: 'var(--color-text-primary)',
                  letterSpacing: '-0.02em',
                }}
              >
                Delete link?
              </h2>
              <p
                style={{
                  margin: '12px 0 0',
                  fontSize: '15px',
                  fontWeight: '600',
                  color: 'var(--color-text-primary)',
                  wordBreak: 'break-word',
                }}
              >
                {linkDeleteConfirm.label}
              </p>
              {linkDeleteConfirm.url && linkDeleteConfirm.label !== linkDeleteConfirm.url && (
                <p
                  style={{
                    margin: '8px 0 0',
                    fontSize: '13px',
                    color: 'var(--color-text-muted)',
                    wordBreak: 'break-all',
                  }}
                >
                  {linkDeleteConfirm.url}
                </p>
              )}
              <p style={{ margin: '12px 0 0', fontSize: '13px', color: 'var(--color-text-muted)' }}>
                This action cannot be undone.
              </p>
              {linkDeleteError && (
                <p style={{ margin: '12px 0 0', fontSize: '13px', color: '#ef4444' }}>{linkDeleteError}</p>
              )}
            </div>
            <div
              style={{
                padding: '16px 24px 22px',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px',
                flexWrap: 'wrap',
              }}
            >
              <button
                type="button"
                disabled={linkDeleteBusy}
                onClick={closeLinkDeleteModal}
                style={{
                  padding: '10px 18px',
                  borderRadius: '8px',
                  border: '1px solid var(--color-border)',
                  backgroundColor: 'transparent',
                  color: 'var(--color-text-secondary)',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: linkDeleteBusy ? 'not-allowed' : 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={linkDeleteBusy}
                onClick={confirmLinkDelete}
                style={{
                  padding: '10px 18px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: '#ef4444',
                  color: '#fff',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: linkDeleteBusy ? 'wait' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {linkDeleteBusy && <Loader2 size={16} style={{ animation: 'spin 0.8s linear infinite' }} />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {!loading &&
        !loadError &&
        filteredCategories.map((cat) => (
          <section
            key={cat.id}
            style={{
              backgroundColor: 'var(--color-surface)',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--color-border)',
              boxShadow: 'var(--shadow-sm)',
              padding: '22px 24px',
              marginBottom: '24px',
            }}
          >
            <h2
              style={{
                fontSize: '17px',
                fontWeight: '700',
                margin: '0 0 18px 0',
                color: 'var(--color-text-primary)',
                letterSpacing: '-0.02em',
              }}
            >
              {cat.name}
            </h2>
            {(cat.resource_links || []).length === 0 ? (
              <p style={{ margin: 0, fontSize: '14px', color: 'var(--color-text-muted)' }}>
                No links in this category yet.
              </p>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                  gap: '14px',
                }}
              >
                {(cat.resource_links || []).map((link) => (
                  <ResourceLinkCard
                    key={link.id}
                    link={link}
                    categories={categories}
                    canEditResources={canEditResources}
                    editingId={editingLinkId}
                    setEditingId={setEditingLinkId}
                    editDraft={editDraft}
                    setEditDraft={setEditDraft}
                    onSaved={onSaved}
                    onRequestDelete={openLinkDeleteModal}
                  />
                ))}
              </div>
            )}
          </section>
        ))}
    </div>
  )
}
